import {
  memo,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type KeyboardEventHandler,
  type Ref,
} from 'react'
import { AlertTriangle } from 'lucide-react'
import { LayerErrorChip } from '@/components/property/calendar/layer-error-chip'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import type { DayAxis } from './day-axis'
import {
  LANE_PITCH,
  LANE_TOP,
  MARKER_BOTTOM,
  errorLane,
  hasNotes,
  rowHeight,
  type GridDensity,
  type PortfolioRow,
} from './layout'
import { parseCalendarDate } from '@/lib/calendar-date'
import {
  INERT_ATTRIBUTE,
  ITEM_ATTRIBUTE,
  isFocusVisible,
  type PortfolioItemActions,
} from './item-actions'
import { dayIso, dayNumber } from './windows'
import { PortfolioBarView } from './portfolio-bar'
import { PortfolioMarkerView } from './portfolio-marker'
import { PropertyCell } from './property-cell'
import { useRowDrag } from './use-row-drag'
import type { DragRange } from '@/components/calendar/use-drag-select'

/** The sticky day header's height. */
const HEADER_HEIGHT = 56

/**
 * A run of days the grid has no answer for (CAP-1416): a window still being
 * read shows the loading skeleton, one that failed says so with a retry. Never
 * drawn as free days.
 */
export interface GridGap {
  key: string
  /** Axis indices, both included. */
  start: number
  end: number
  state: 'loading' | 'error'
  /** "5 Oct - 25 Oct 2026" */
  label: string
  /** For a failed window. */
  onRetry?: () => void
  retrying?: boolean
}

interface PortfolioGridProps {
  axis: DayAxis
  rows: PortfolioRow[]
  /** Today's index on the axis, or null when today is not on it. */
  todayIndex: number | null
  density: GridDensity
  /** The scrolling element, for `useVisibleDays`. */
  scrollRef: Ref<HTMLDivElement>
  /** The axis indices to put in the DOM (`useVisibleDays`). */
  renderStart: number
  renderEnd: number
  /** The axis index of the day at the left edge, or null before the grid is measured. */
  edge: number | null
  /** The axis index of the last day in view, or null before the grid is measured. */
  visibleEnd: number | null
  gaps: readonly GridGap[]
  /** Whether a window on screen is still being read. */
  busy: boolean
  onKeyDown: KeyboardEventHandler<HTMLDivElement>
  /** What a click on an item or an empty day does: one stable object (see `PortfolioItemActions`). */
  actions: PortfolioItemActions
}

/**
 * Every property on one timeline: a sticky header of days, and one row per
 * property with its sticky photo-and-nickname cell and a track of bars.
 *
 * Built for months of days (CAP-1416), and drawing a few weeks of them:
 * - day lines, weekend shading and the column grid are CSS backgrounds on each
 *   track, not an element per day (18 rows x hundreds of days would be
 *   thousands of nodes);
 * - only the days on screen plus a buffer reach the DOM (`renderStart` to
 *   `renderEnd`), bars, header days and skeletons alike, while each track keeps
 *   the axis's full width so the scrollbar is honest;
 * - a row's height comes from its lanes over everything read, so rows never
 *   change height while scrolling.
 */
export function PortfolioGrid({
  axis,
  rows,
  todayIndex,
  density,
  scrollRef,
  renderStart,
  renderEnd,
  edge,
  visibleEnd,
  gaps,
  busy,
  onKeyDown,
  actions,
}: PortfolioGridProps) {
  const dayCount = axis.days.length
  const { dayWidth, asideWidth, narrow } = density
  const trackWidth = dayCount * dayWidth
  const totalWidth = asideWidth + trackWidth

  // One style object for every track: the day lines and, from the first
  // Monday, five plain days and two shaded weekend days, repeating.
  const trackBackground = useMemo<CSSProperties>(
    () => ({
      backgroundImage: [
        'linear-gradient(to right, var(--color-border) 1px, transparent 1px)',
        `linear-gradient(to right, transparent ${5 * dayWidth}px, rgba(255, 255, 255, 0.03) ${5 * dayWidth}px)`,
      ].join(', '),
      backgroundSize: `${dayWidth}px 100%, ${7 * dayWidth}px 100%`,
      backgroundPosition: `0 0, ${axis.mondayOffset * dayWidth}px 0`,
    }),
    [dayWidth, axis.mondayOffset]
  )

  const headerDays = axis.days.slice(renderStart, renderEnd + 1)
  const axisStart = dayNumber(axis.from) ?? 0
  const count = rows.length

  // Only the gaps that reach the rendered days, so a long session does not
  // pile up skeletons for windows far off the screen.
  const shownGaps = gaps.filter(gap => gap.end >= renderStart && gap.start <= renderEnd)
  // A new array only when the loading runs on screen change, so the memoised
  // rows do not re-render for nothing.
  const loadingKey = shownGaps
    .filter(gap => gap.state === 'loading')
    .map(gap => `${gap.key}:${gap.start}:${gap.end}`)
    .join('|')
  const loading = useMemo(
    () => shownGaps.filter(gap => gap.state === 'loading'),
    // `loadingKey` says exactly what `loading` holds.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [loadingKey]
  )
  const failed = shownGaps.filter(gap => gap.state === 'error')

  return (
    <div
      ref={scrollRef}
      role="region"
      tabIndex={0}
      aria-label="Calendar of every property. Arrow keys move a day, Page Up and Page Down a week, Home goes to today."
      aria-busy={busy || undefined}
      data-testid="portfolio-grid"
      onKeyDown={onKeyDown}
      className={cn(
        // `min-h-0`: the calendar is a column no taller than the screen, and the
        // grid takes what the toolbar leaves, scrolling inside it.
        'relative min-h-0 overflow-auto overscroll-x-contain border-border bg-surface focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-mint',
        // On a phone the grid runs edge to edge, past the page's padding.
        narrow ? '-mx-4 border-y' : 'rounded-card border'
      )}
    >
      <div className="relative" style={{ width: totalWidth }}>
        <div
          className="sticky top-0 z-20 flex border-b border-border bg-surface"
          style={{ width: totalWidth, height: HEADER_HEIGHT }}
        >
          <div
            className="sticky left-0 z-30 flex shrink-0 items-center border-r border-border bg-surface px-3.5 text-xs text-textMuted"
            style={{ width: asideWidth }}
          >
            {narrow
              ? `${count} ${count === 1 ? 'home' : 'homes'}`
              : `${count} ${count === 1 ? 'property' : 'properties'}`}
          </div>
          <div className="relative shrink-0" style={{ width: trackWidth }}>
            {headerDays.map((day, offset) => {
              const index = renderStart + offset
              const isToday = index === todayIndex
              return (
                <div
                  key={day.iso}
                  data-testid="portfolio-day"
                  data-day={day.iso}
                  data-today={isToday ? 'true' : undefined}
                  aria-current={isToday ? 'date' : undefined}
                  className={cn(
                    'absolute top-1 flex h-12 flex-col items-center justify-center gap-px rounded-lg',
                    isToday
                      ? 'bg-mint text-bg'
                      : day.isWeekend
                        ? 'bg-white/[0.03] text-white/60'
                        : 'text-white/85'
                  )}
                  style={{ left: index * dayWidth + 2, width: dayWidth - 4 }}
                >
                  <span className="text-[11px] leading-[13px]">
                    {narrow ? day.weekday.charAt(0) : day.weekday}
                  </span>
                  <span className="text-[15px] font-semibold leading-[18px] tabular-nums">
                    {day.date}
                  </span>
                  <span className="text-[10px] leading-3">{day.month}</span>
                </div>
              )
            })}
          </div>
        </div>

        <div className="relative">
          {todayIndex !== null && (
            <div
              aria-hidden="true"
              data-testid="portfolio-today-column"
              className="pointer-events-none absolute inset-y-0 z-[1] bg-mint/[0.07] shadow-[inset_1px_0_0_var(--color-mintBorder),inset_-1px_0_0_var(--color-mintBorder)]"
              style={{ left: asideWidth + todayIndex * dayWidth, width: dayWidth }}
            />
          )}
          {rows.map(row => (
            <PortfolioRowView
              key={row.propertyId}
              row={row}
              density={density}
              trackWidth={trackWidth}
              trackBackground={trackBackground}
              renderStart={renderStart}
              renderEnd={renderEnd}
              axisStart={axisStart}
              edge={edge}
              visibleEnd={visibleEnd}
              loading={loading}
              actions={actions}
            />
          ))}
          {failed.map(gap => (
            <WindowError key={gap.key} gap={gap} density={density} />
          ))}
        </div>
      </div>
    </div>
  )
}

/**
 * A window that could not be read: over its days in every row, said once,
 * with a retry for those days alone. The rest of the grid stays as it is.
 */
function WindowError({ gap, density }: { gap: GridGap; density: GridDensity }) {
  const { dayWidth, asideWidth } = density
  return (
    <div
      role="alert"
      data-testid="portfolio-window-error"
      className="absolute inset-y-0 z-[4] border-x border-danger/40 bg-surface"
      style={{
        left: asideWidth + gap.start * dayWidth,
        width: (gap.end - gap.start + 1) * dayWidth,
      }}
    >
      <div
        className="sticky flex w-max max-w-full flex-col items-start gap-2 px-3 py-3 text-sm text-danger"
        style={{ top: HEADER_HEIGHT, left: asideWidth }}
      >
        <span className="inline-flex items-center gap-2">
          <AlertTriangle className="h-4 w-4 shrink-0" aria-hidden="true" />
          Could not load {gap.label}.
        </span>
        <Button variant="outline" size="sm" disabled={gap.retrying} onClick={gap.onRetry}>
          Retry
        </Button>
      </div>
    </div>
  )
}

interface PortfolioRowViewProps {
  row: PortfolioRow
  density: GridDensity
  trackWidth: number
  trackBackground: CSSProperties
  renderStart: number
  renderEnd: number
  /** The day number of the axis's first day. */
  axisStart: number
  edge: number | null
  visibleEnd: number | null
  /** Runs of days still being read: the loading skeleton, over anything drawn there. */
  loading: readonly GridGap[]
  actions: PortfolioItemActions
}

/**
 * How near the left edge (in days) a bar's label is made sticky. The edge is
 * read once per frame and the row re-rendered once per day crossed, so the
 * margin covers a fast fling between two renders.
 */
const STICKY_MARGIN_DAYS = 3

const PortfolioRowView = memo(function PortfolioRowView({
  row,
  density,
  trackWidth,
  trackBackground,
  renderStart,
  renderEnd,
  axisStart,
  edge,
  visibleEnd,
  loading,
  actions,
}: PortfolioRowViewProps) {
  const height = rowHeight(row, density)
  const inView = (bar: { firstDay: number; lastDay: number }) =>
    bar.lastDay >= renderStart && bar.firstDay <= renderEnd
  const bars = row.bars.filter(inView)
  const gaps = row.gaps.filter(inView)
  const markers = row.markers.filter(marker => marker.day >= renderStart && marker.day <= renderEnd)
  const sticky = (bar: { firstDay: number; lastDay: number }) =>
    edge === null ||
    (bar.firstDay <= edge + STICKY_MARGIN_DAYS && bar.lastDay >= edge - STICKY_MARGIN_DAYS)
  // Only what is in view takes the Tab key: an item in the buffer either side
  // (or under the sticky property column) would scroll the grid to itself.
  const focusable = (first: number, last: number) =>
    edge === null || visibleEnd === null || (last >= edge && first <= visibleEnd)
  // A click on the track is a day only when the press started on an empty day
  // too: a press on an item released over an empty day is not that day. Held
  // as the day number pressed, which the click then opens, so a press that
  // wobbles across a day line still opens the day it began on.
  const pressedDay = useRef<number | null>(null)
  // The keyboard's way to a day with nothing on it: the track is one tab stop,
  // Left and Right move a day cursor (a day number, so it holds while windows
  // come and go), Enter or Space opens that day's list. Shown only for
  // keyboard focus; a click goes straight to its day.
  const [cursor, setCursor] = useState<number | null>(null)
  const dayCount = Math.round(trackWidth / density.dayWidth)
  const cursorIndex = cursor === null ? null : cursor - axisStart
  const cursorId = `portfolio-day-cursor-${row.propertyId}`
  // Drag-to-block (CAP-1419): a press on an empty day dragged across others
  // opens "Block dates" on them for this property. Its range is this row's
  // state alone, so a drag re-renders the row it is on and no other.
  const drag = useRowDrag({
    axisStart,
    dayCount,
    dayWidth: density.dayWidth,
    asideWidth: density.asideWidth,
    onStart: actions.tips.hide,
    // The click that ends a drag is not a click on a day.
    onDragged: () => {
      pressedDay.current = null
    },
    onSelect: (first, last, track) => actions.openNewBlock(row.propertyId, first, last, track),
  })
  return (
    <div
      data-testid="portfolio-row"
      data-property-id={row.propertyId}
      data-lanes={row.laneCount}
      className="flex border-b border-border last:border-b-0"
      style={{ width: density.asideWidth + trackWidth }}
    >
      <PropertyCell
        propertyId={row.propertyId}
        name={row.name}
        thumbnailUrl={row.thumbnailUrl}
        narrow={density.narrow}
        width={density.asideWidth}
        height={height}
      />
      {/* The track: a click on an empty day opens that day's list for this
          property (CAP-1418). Items stop their own clicks, and the skeleton
          and the chips are not days. A press on an empty day dragged across
          others blocks them (CAP-1419); one that starts on an item or an
          inert overlay is not a drag. */}
      <div
        data-testid="portfolio-track"
        tabIndex={0}
        role="group"
        aria-label={`${row.name}: days. Left and right arrows choose a day, Enter opens it.`}
        aria-activedescendant={cursorIndex !== null ? cursorId : undefined}
        className="relative shrink-0 cursor-cell select-none focus-visible:outline-hidden"
        style={{ width: trackWidth, height, ...trackBackground }}
        onFocus={event => {
          if (event.target !== event.currentTarget || !isFocusVisible(event.currentTarget)) return
          setCursor(axisStart + (edge ?? 0))
        }}
        onBlur={event => {
          if (event.target === event.currentTarget) setCursor(null)
        }}
        onKeyDown={event => {
          if (event.target !== event.currentTarget || cursor === null) return
          if (event.altKey || event.metaKey || event.ctrlKey) return
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault()
            actions.openDay(row.propertyId, cursor - axisStart, event.currentTarget)
            return
          }
          const step = event.key === 'ArrowLeft' ? -1 : event.key === 'ArrowRight' ? 1 : 0
          if (!step) return
          event.preventDefault()
          const index = Math.min(dayCount - 1, Math.max(0, cursor - axisStart + step))
          setCursor(axisStart + index)
          // Kept in view: the grid scrolls a day when the cursor leaves it.
          const grid = event.currentTarget.closest<HTMLElement>('[data-testid="portfolio-grid"]')
          if (grid && edge !== null && visibleEnd !== null) {
            if (index < edge) grid.scrollLeft -= (edge - index) * density.dayWidth
            else if (index > visibleEnd) grid.scrollLeft += (index - visibleEnd) * density.dayWidth
          }
        }}
        onPointerDown={event => {
          const target = event.target as Element
          const onDay = !target.closest(`[${ITEM_ATTRIBUTE}], [${INERT_ATTRIBUTE}]`)
          // Only a press on an empty day starts a drag (a finger never does:
          // it scrolls, and the day's sheet has "Block dates").
          pressedDay.current = onDay ? drag.press(event) : null
        }}
        onClick={event => {
          const target = event.target as Element
          const day = target.closest(`[${ITEM_ATTRIBUTE}], [${INERT_ATTRIBUTE}]`)
            ? null
            : pressedDay.current
          pressedDay.current = null
          if (day === null) return
          actions.openDay(row.propertyId, day - axisStart, event.currentTarget)
        }}
      >
        {cursor !== null && cursorIndex !== null && (
          <div
            id={cursorId}
            role="button"
            aria-label={dayWords(cursor)}
            data-testid="portfolio-day-cursor"
            className="pointer-events-none absolute inset-y-0.5 z-[5] rounded-md ring-2 ring-inset ring-mint"
            style={{ left: cursorIndex * density.dayWidth, width: density.dayWidth }}
          />
        )}
        {drag.range && (
          <DragRangeView
            range={drag.range}
            axisStart={axisStart}
            dayCount={dayCount}
            density={density}
          />
        )}
        {gaps.map(gap => (
          <PortfolioBarView
            key={gap.key}
            bar={gap}
            propertyId={row.propertyId}
            actions={actions}
            density={density}
            sticky={sticky(gap)}
            focusable={focusable(gap.firstDay, gap.lastDay)}
          />
        ))}
        {bars.map(bar => (
          <PortfolioBarView
            key={bar.key}
            bar={bar}
            propertyId={row.propertyId}
            actions={actions}
            density={density}
            sticky={sticky(bar)}
            focusable={focusable(bar.firstDay, bar.lastDay)}
          />
        ))}
        {markers.map(marker => (
          <PortfolioMarkerView
            key={marker.key}
            marker={marker}
            propertyId={row.propertyId}
            actions={actions}
            density={density}
            top={height - MARKER_BOTTOM}
            focusable={focusable(marker.day, marker.day)}
          />
        ))}
        {loading.map(gap => (
          // Above the bars: a bar from a window read already may run into days
          // not read yet, and what else is on those days is not known.
          <div
            key={gap.key}
            aria-hidden="true"
            {...{ [INERT_ATTRIBUTE]: '' }}
            data-testid="portfolio-loading-days"
            className="absolute inset-y-0 z-[3] flex cursor-default flex-col justify-center gap-2 bg-surface px-2.5"
            style={{
              left: gap.start * density.dayWidth,
              width: (gap.end - gap.start + 1) * density.dayWidth,
            }}
          >
            <span className="block h-3 w-[62%] animate-pulse rounded-md bg-surface2 motion-reduce:animate-none" />
            <span className="block h-2 w-[34%] animate-pulse rounded-md bg-surface2/70 motion-reduce:animate-none" />
          </div>
        ))}
        {hasNotes(row) && (
          // A full-width strip in its own lane, with the chips sticky at the
          // property column's edge so they stay in view wherever the grid is.
          <div
            className="pointer-events-none absolute inset-x-0 flex"
            style={{ top: LANE_TOP + errorLane(row) * LANE_PITCH }}
          >
            <div
              {...{ [INERT_ATTRIBUTE]: '' }}
              className="pointer-events-auto sticky flex cursor-default gap-1.5 whitespace-nowrap px-1.5"
              style={{ left: density.asideWidth }}
            >
              {row.errors.map(error => (
                <LayerErrorChip
                  key={error.layer}
                  testId={`portfolio-layer-error-${error.layer}`}
                  message={error.message}
                  className="py-0.5"
                />
              ))}
              {row.caveats.map(caveat => (
                // A dated or partial answer, in the property tab's amber words.
                // Not a live region: it is on screen, and eighteen rows saying
                // one sentence at once would be noise for a screen reader.
                <span
                  key={`${caveat.layer}:${caveat.message}`}
                  data-testid={`portfolio-layer-caveat-${caveat.layer}`}
                  className="inline-flex items-center gap-1.5 rounded-full border border-amber/50 bg-amber/10 px-2.5 py-0.5 text-xs text-amber"
                >
                  <AlertTriangle className="h-3 w-3 shrink-0" aria-hidden="true" />
                  {caveat.message}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  )
})

/**
 * The days a drag covers on its row, as the approved mock draws them: a mint
 * band over the bars with the nights it would block. Clipped to the grid's
 * days, though a drag holds day numbers that may run past them for a moment.
 */
function DragRangeView({
  range,
  axisStart,
  dayCount,
  density,
}: {
  range: DragRange<number>
  axisStart: number
  dayCount: number
  density: GridDensity
}) {
  const { dayWidth, narrow } = density
  const start = Math.max(0, range.first - axisStart)
  const end = Math.min(dayCount - 1, range.last - axisStart)
  if (end < start) return null
  const nights = range.last - range.first + 1
  return (
    <div
      aria-hidden="true"
      data-testid="portfolio-drag-range"
      data-first={dayIso(range.first)}
      data-last={dayIso(range.last)}
      className="pointer-events-none absolute inset-y-[3px] z-[5] flex items-center justify-center overflow-hidden whitespace-nowrap rounded-md border border-mint bg-mint/[0.18] text-[11px] font-semibold text-text"
      style={{ left: start * dayWidth + 1, width: (end - start + 1) * dayWidth - 2 }}
    >
      {narrow ? nights : `${nights} ${nights === 1 ? 'night' : 'nights'}`}
    </div>
  )
}

/** Rows of placeholders the shape of the grid, while the calendar is read. */
export function PortfolioGridSkeleton({ density }: { density: GridDensity }) {
  return (
    <div
      role="status"
      aria-busy="true"
      data-testid="portfolio-skeleton"
      className={cn(
        'overflow-hidden border-border bg-surface',
        density.narrow ? '-mx-4 border-y' : 'rounded-card border'
      )}
    >
      <span className="sr-only">Loading the calendar</span>
      <div className="flex border-b border-border" style={{ height: HEADER_HEIGHT }}>
        <div className="shrink-0 border-r border-border" style={{ width: density.asideWidth }} />
        <div className="flex flex-1 items-center gap-2 px-2">
          <div className="h-10 flex-1 animate-pulse rounded-lg bg-surface2 motion-reduce:animate-none" />
        </div>
      </div>
      {Array.from({ length: 8 }, (_, index) => (
        <div
          key={index}
          data-testid="portfolio-skeleton-row"
          className="flex border-b border-border last:border-b-0"
          style={{ height: density.rowMinHeight }}
        >
          <div
            className="flex shrink-0 items-center gap-2.5 border-r border-border px-3"
            style={{ width: density.asideWidth }}
          >
            {!density.narrow && (
              <div className="h-10 w-10 shrink-0 animate-pulse rounded-lg bg-surface2 motion-reduce:animate-none" />
            )}
            <div className="h-3 flex-1 animate-pulse rounded-md bg-surface2 motion-reduce:animate-none" />
          </div>
          <div className="flex flex-1 items-center px-2">
            <div
              className="h-[22px] animate-pulse rounded-md bg-surface2 motion-reduce:animate-none"
              style={{ width: `${30 + ((index * 37) % 50)}%`, marginLeft: `${(index * 23) % 30}%` }}
            />
          </div>
        </div>
      ))}
    </div>
  )
}

/** "Wednesday, October 28", as the day's own list is headed. */
function dayWords(day: number): string {
  const date = parseCalendarDate(dayIso(day))
  return date
    ? date.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })
    : dayIso(day)
}
